import { Ref, useRef, useState } from 'react';
import { Box, IconButton } from '@mui/material';
import { faPaperPlaneTop } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { isEmpty, noop, trim } from 'lodash';
import classNames from 'classnames';
import { ChannelMentionPopover } from './ChannelMentionPopover';
import { ChannelMentionConfirmation } from './ChannelMentionConfirmation';
import { MentionTextField } from './MentionTextField';
import {
  containsChannelMention,
  getChannelMentionTriggerPosition,
  insertChannelMention,
} from '../utils/channelMention';

interface AddReplyProps {
  initialText?: string;
  submit?: (text: string) => void;
  inputRef?: Ref<HTMLTextAreaElement>;
  memberCount?: number;
}

export const AddReply = ({
  initialText = '',
  submit = noop,
  inputRef = undefined,
  memberCount,
}: AddReplyProps) => {
  const [text, setText] = useState(initialText);
  const [mentionAnchor, setMentionAnchor] = useState<HTMLElement | null>(null);
  const [triggerStart, setTriggerStart] = useState<number>(0);
  const [showConfirmation, setShowConfirmation] = useState(false);
  const internalRef = useRef<HTMLTextAreaElement>(null);
  const textareaRef =
    (inputRef as React.RefObject<HTMLTextAreaElement>) || internalRef;

  const handleChange = (event: React.ChangeEvent<HTMLTextAreaElement>) =>
    setText(event.target?.value);

  const handleKeyUp = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    const target = e.target as HTMLTextAreaElement;
    const cursorPos = target.selectionStart || 0;

    const triggerPos = getChannelMentionTriggerPosition(text, cursorPos);

    if (triggerPos >= 0) {
      setTriggerStart(triggerPos);
      setMentionAnchor(target);
    } else {
      setMentionAnchor(null);
    }
  };

  const handleMentionSelect = () => {
    const cursorPos = textareaRef.current?.selectionStart || text.length;
    const newText = insertChannelMention(text, cursorPos, triggerStart);
    setText(newText);
    setMentionAnchor(null);
  };

  const handleSubmit = () => {
    // TODO: Re-enable @channel feature when ready
    // if (containsChannelMention(text)) {
    //   setShowConfirmation(true);
    // } else {
    //   submit(text);
    //   setText('');
    // }
    submit(text);
    setText('');
  };

  const handleConfirmSend = () => {
    setShowConfirmation(false);
    submit(text);
    setText('');
  };

  return (
    <Box className="border-t flex pt-5">
      <MentionTextField
        ref={textareaRef}
        className="w-full mr-2"
        placeholder="Message"
        onChange={handleChange}
        // TODO: Re-enable @channel feature when ready
        // onKeyUp={handleKeyUp}
        value={text}
      />
      <IconButton
        className={classNames([
          'bg-purple-1',
          'disabled:bg-gray-2',
          'mb-1',
          'text-white',
          'w-[50px]',
          'h-[50px]',
          'rounded-full',
          'self-end',
          { hidden: isEmpty(text) },
        ])}
        aria-label="Send the reply"
        disabled={isEmpty(trim(text))}
        onClick={handleSubmit}
      >
        <FontAwesomeIcon size="2xs" icon={faPaperPlaneTop} />
      </IconButton>

      {/* TODO: Re-enable @channel feature when ready
      <ChannelMentionPopover
        anchorEl={mentionAnchor}
        onSelect={handleMentionSelect}
        onClose={() => setMentionAnchor(null)}
      />

      <ChannelMentionConfirmation
        open={showConfirmation}
        memberCount={memberCount}
        onConfirm={handleConfirmSend}
        onCancel={() => setShowConfirmation(false)}
      />
      */}
    </Box>
  );
};
